<template>
    <div id="survey-submissions">
        <v-card>
            <v-card-title>
                {{ survey ? survey.title : $t("Kyselyn vastaukset") }}
            </v-card-title>


            <v-card-text v-if="loading">
                <!-- Skeleton Loader -->
                <v-skeleton-loader type="heading" class="mb-4"></v-skeleton-loader>
                <v-skeleton-loader type="table" class="mb-4"></v-skeleton-loader>
            </v-card-text>

            <v-card-text v-else-if="!survey" class="text-center py-8">
                <v-icon size="64" color="grey lighten-2">{{ icons.mdiAlertCircle }}</v-icon>
                <p class="grey--text mt-2">{{ $t("Kyselyä ei löytynyt") }}</p>
            </v-card-text>

            <!-- Collapsible Analytics Section -->
            <v-card-text v-else>
                <v-expansion-panels v-model="analyticsPanel">
                    <v-expansion-panel>
                        <v-expansion-panel-header>
                            <div class="d-flex align-center">
                                <v-icon left>{{ icons.mdiChartBar }}</v-icon>
                                <span class="text-h6">
                                    {{ $t("Raportti") }}
                                </span>
                                <div class="ml-5" v-if="loadingAnalytics">
                                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                                </div>
                            </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content :class="{ 'loading-analytics': loadingAnalytics }">

                            <!-- Analytics Content -->
                            <div ref="chartsContainer">
                                <!-- Summary Cards -->
                                <v-row class="mb-4">
                                    <v-col cols="12" sm="6" md="4">
                                        <v-card outlined>
                                            <v-card-subtitle class="pb-0">{{ $t("Vastauksia") }}</v-card-subtitle>
                                            <v-card-text>
                                                <div class="text-h3 primary--text">{{ totalCount }}</div>
                                            </v-card-text>
                                        </v-card>
                                    </v-col>
                                    <v-col cols="12" sm="6" md="4">
                                        <v-card outlined>
                                            <v-card-subtitle class="pb-0">{{ $t("Viimeisin vastaus")
                                                }}</v-card-subtitle>
                                            <v-card-text>
                                                <div class="text-h6">{{ lastResponseDate }}</div>
                                            </v-card-text>
                                        </v-card>
                                    </v-col>
                                </v-row>

                                <!-- Question Analytics -->
                                <v-row>
                                    <v-col v-for="field in selectFields" :key="field.id" cols="12" sm="12" md="6"
                                        lg="4">
                                        <v-card outlined :data-field-id="field.id">
                                            <v-card-title class="d-flex justify-space-between align-center">
                                                <span>{{ field.label }}</span>
                                                <v-btn icon small @click="exportSingleChart(field.id, field.label)"
                                                    class="no-export">
                                                    <v-icon small>{{ icons.mdiDownload }}</v-icon>
                                                </v-btn>
                                            </v-card-title>
                                            <v-card-text>
                                                <div v-for="option in field.options" :key="option.id" class="mb-2">
                                                    <div class="d-flex justify-space-between mb-1">
                                                        <span>{{ option.label }}</span>
                                                        <span class="font-weight-bold">
                                                            {{ getOptionCount(field.id, option.id) }}
                                                            ({{ getOptionPercentage(field.id, option.id) }}%)
                                                        </span>
                                                    </div>
                                                    <v-progress-linear :value="getOptionPercentage(field.id, option.id)"
                                                        height="40" rounded
                                                        :color="isOptionFiltered(field.id, option.id) ? 'primary darken-2' : 'primary'"
                                                        :class="['clickable', { 'active-filter': isOptionFiltered(field.id, option.id) }]"
                                                        @click="toggleFilter(field.id, option.id)">
                                                        <template v-slot:default>
                                                            <span class="white--text">
                                                                {{ getOptionCount(field.id, option.id) }} {{
                                                                    $t('vastausta') }}
                                                            </span>
                                                        </template>
                                                    </v-progress-linear>
                                                </div>
                                            </v-card-text>
                                        </v-card>
                                    </v-col>
                                </v-row>
                                <v-row class="no-export">
                                    <v-col>
                                        <v-btn @click="exportChartsAsPNG" color="primary" small
                                            v-if="analyticsPanel === 0">
                                            <v-icon left>{{ icons.mdiDownload }}</v-icon>
                                            {{ $t('Vie kaaviot kuvana') }}
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>

                <!-- Active Filters Display -->
                <v-row v-if="hasActiveFilters" class="mt-4">
                    <v-col>
                        <div class="d-flex align-center flex-wrap">
                            <span class="mr-2 font-weight-bold">{{ $t("Suodattimet") }}:</span>
                            <v-chip v-for="(optionIds, fieldId) in activeFilters" :key="fieldId" close color="primary"
                                class="mr-2 mb-2" @click:close="removeFilter(fieldId)">
                                {{ getFieldName(fieldId) }}: {{ getOptionLabels(fieldId, optionIds) }}
                            </v-chip>
                            <v-btn small text color="grey" @click="clearAllFilters">
                                {{ $t("Tyhjennä") }}
                            </v-btn>
                        </div>
                    </v-col>
                </v-row>

                <!-- Submissions Table -->
                <div class="mt-4">
                    <!-- Pagination Controls - Top -->
                    <div class="mb-4" v-if="filteredSubmissions.length">
                        <div class="d-flex justify-space-between align-center">
                            <div class="grey--text">
                                {{ $t("Näytetään") }} {{ (currentPage - 1) * pageSize + 1 }}-{{ Math.min(currentPage *
                                    pageSize, totalSubmissions) }} / {{ totalSubmissions }}
                            </div>
                            <v-pagination v-model="currentPage" :length="totalPages" :total-visible="7" circle
                                @input="fetchSubmissions"></v-pagination>
                        </div>
                    </div>

                    <div v-if="survey && submissions.length > 0" class="mb-2">
                        <v-btn @click="excelExport" color="primary" small class="mr-2"
                            :loading="loadingExport ? 'white' : false">
                            <v-icon style=" color: white">{{ icons.mdiMicrosoftExcel }}</v-icon>
                            <span class="pl-2 white--text">{{ $t('Vie vastaukset') }}</span>
                        </v-btn>

                    </div>

                    <v-card-text v-if="submissions.length === 0" class="text-center py-8">
                        <v-icon size="64" color="grey lighten-2">{{ icons.mdiClipboardTextOffOutline }}</v-icon>
                        <p class="grey--text mt-2">{{ $t("Ei vastauksia") }}</p>
                    </v-card-text>

                    <v-simple-table v-else fixed-header>
                        <template v-slot:default>
                            <thead>
                                <tr>
                                    <th v-if="!survey.form.anonymous">
                                        <h3>{{ $t("Käyttäjä") }}</h3>
                                    </th>
                                    <th>
                                        <h3>{{ $t("Vastausaika") }}</h3>
                                    </th>
                                    <th v-for="field in survey.form.fields" :key="'th-' + field.id">
                                        <h3>{{ field.label }}</h3>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="submission in filteredSubmissions" :key="submission.id">
                                    <td v-if="!survey.form.anonymous">
                                        {{ submission.user }}
                                    </td>
                                    <td>
                                        {{ getDateTime(submission.submit_date) }}
                                    </td>
                                    <td v-for="field in survey.form.fields"
                                        :key="'answer-' + submission.id + '-' + field.id">
                                        <div style="white-space: pre-line;">
                                            {{ getFormattedAnswer(field, submission) }}
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </template>
                    </v-simple-table>

                    <!-- Pagination Controls -->
                    <div class="mt-4" v-if="filteredSubmissions.length">
                        <div class="d-flex justify-space-between align-center">
                            <div class="grey--text">
                                {{ (currentPage - 1) * pageSize + 1 }}-{{ Math.min(currentPage *
                                    pageSize, totalSubmissions) }} / {{ totalSubmissions }}
                            </div>
                            <v-pagination v-model="currentPage" :length="totalPages" :total-visible="7" circle
                                @input="fetchSubmissions"></v-pagination>
                        </div>
                    </div>
                </div>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import { onMounted, ref, computed, watch } from "@vue/composition-api";
import { useRouter } from "@/@core/utils";
import adminApi from "@/api/admin";
import {
    mdiArrowLeft,
    mdiClipboardTextOffOutline,
    mdiAlertCircle,
    mdiChartBar,
    mdiMicrosoftExcel,
    mdiDownload,
} from "@mdi/js";
import { useTranslations } from "@core/libs/i18n";
import { getDateTime } from "@/@core/utils/datetime_helpers";
import { toPng } from 'html-to-image';
import { i18n } from "@/plugins/i18n";

export default {
    components: {},
    props: ["id"],
    setup(props) {
        const { router } = useRouter();

        const survey = ref(null);
        const submissions = ref([]);
        const analytics = ref(null);
        const loading = ref(false);
        const loadingSubmissions = ref(false);
        const loadingAnalytics = ref(false);
        const loadingExport = ref(false);
        const analyticsPanel = ref(null);
        const activeFilters = ref({});
        const currentPage = ref(1);
        const pageSize = ref(50);
        const totalSubmissions = ref(0);
        const totalPages = ref(0);

        const fetchSurvey = async () => {
            const response = await adminApi.getSurvey(props.id);
            if (response.ok) {
                survey.value = response.data;
            }
        };

        const fetchAnalytics = async () => {
            loadingAnalytics.value = true;

            // Build filters parameter
            const filtersParam = Object.keys(activeFilters.value).length > 0
                ? JSON.stringify(activeFilters.value)
                : null;

            const response = await adminApi.getSurveySubmissionsAnalytics(props.id, filtersParam);
            if (response.ok) {
                analytics.value = response.data;
            }

            loadingAnalytics.value = false;
        };

        const fetchSubmissions = async (page = 1) => {
            loadingSubmissions.value = true;

            // Build filters parameter
            const filtersParam = Object.keys(activeFilters.value).length > 0
                ? JSON.stringify(activeFilters.value)
                : null;

            const response = await adminApi.getSurveySubmissions(
                props.id,
                page,
                pageSize.value,
                filtersParam
            );

            if (response.ok) {
                submissions.value = response.data.results;
                totalSubmissions.value = response.data.count;
                totalPages.value = Math.ceil(response.data.count / pageSize.value);
                currentPage.value = page;
            }

            loadingSubmissions.value = false;
        };

        const fetchData = async () => {
            loading.value = true;
            await fetchSurvey();
            await fetchSubmissions(1);
            loading.value = false;
        };

        const getFieldResponse = (field, submission) => {
            return submission.responses.find(r => r.field_id === field.id);
        };

        const getFormattedAnswer = (field, submission) => {
            const response = getFieldResponse(field, submission);
            if (!response || response.value === null || response.value === undefined) {
                return '-';
            }

            // Text fields (1: short text, 2: long text)
            if (field.field_type === 1 || field.field_type === 2) {
                if (response.value) {
                    if (field.datatype == 'date') {
                        try {
                            const date = new Date(response.value);
                            if (!isNaN(date.getTime())) {
                                return date.toLocaleDateString(i18n.locale);
                            }
                        }
                        catch { }
                    }

                    return response.value;
                }
                return response.value || '-';
            }

            // Choice fields (3: radio, 4: checkbox)
            if (field.field_type === 3 || field.field_type === 4) {
                if (!Array.isArray(response.value) || response.value.length === 0) {
                    return '-';
                }

                const selectedOptions = field.options.filter(opt =>
                    response.value.includes(opt.id)
                );
                return selectedOptions.map(opt => opt.label).join(', ') || '-';
            }

            return String(response.value);
        };

        // Get only radio (3) and checkbox (4) fields for analytics
        const selectFields = computed(() => {
            if (!survey.value?.form?.fields) return [];
            return survey.value.form.fields.filter(f => f.field_type === 3 || f.field_type === 4);
        });

        // Get last response date from analytics (always shows all data)
        const lastResponseDate = computed(() => {
            if (!analytics.value || !analytics.value.last_response) return '-';
            return getDateTime(analytics.value.last_response);
        });

        // Get total submission count from analytics
        const totalCount = computed(() => {
            return analytics.value?.total_count || 0;
        });

        // Count how many submissions selected this option (from analytics - always all data)
        const getOptionCount = (fieldId, optionId) => {
            if (!analytics.value || !analytics.value.fields) return 0;
            const fieldStats = analytics.value.fields[fieldId];
            if (!fieldStats || !fieldStats.options) return 0;
            const optionStats = fieldStats.options[optionId];
            return optionStats?.count || 0;
        };

        // Calculate percentage for this option (from analytics - always all data)
        const getOptionPercentage = (fieldId, optionId) => {
            if (!analytics.value || !analytics.value.fields) return 0;
            const fieldStats = analytics.value.fields[fieldId];
            if (!fieldStats || !fieldStats.options) return 0;
            const optionStats = fieldStats.options[optionId];
            return optionStats?.percentage || 0;
        };

        // Filtered submissions are what comes from the API (already filtered server-side)
        const filteredSubmissions = computed(() => {
            return submissions.value;
        });

        // Check if any filters are active
        const hasActiveFilters = computed(() => {
            return Object.keys(activeFilters.value).length > 0;
        });

        // Check if specific option is filtered
        const isOptionFiltered = (fieldId, optionId) => {
            return activeFilters.value[fieldId]?.includes(optionId) || false;
        };

        // Toggle filter on/off and refetch both analytics and submissions
        const toggleFilter = (fieldId, optionId) => {
            if (!activeFilters.value[fieldId]) {
                // Add new filter for this field
                activeFilters.value[fieldId] = [optionId];
            } else {
                const currentFilters = activeFilters.value[fieldId];
                const index = currentFilters.indexOf(optionId);

                if (index > -1) {
                    // Remove this option from filters
                    currentFilters.splice(index, 1);
                    if (currentFilters.length === 0) {
                        delete activeFilters.value[fieldId];
                    }
                } else {
                    // Add this option to existing filters for this field
                    currentFilters.push(optionId);
                }
            }
            // Trigger reactivity and refetch both analytics and submissions
            activeFilters.value = { ...activeFilters.value };
            fetchAnalytics(); // Update charts with filtered data
            fetchSubmissions(1); // Reset to page 1 when filters change
        };

        // Remove a single filter and refetch both analytics and submissions
        const removeFilter = (fieldId) => {
            delete activeFilters.value[fieldId];
            activeFilters.value = { ...activeFilters.value };
            fetchAnalytics(); // Update charts with filtered data
            fetchSubmissions(1); // Reset to page 1 when filters change
        };

        // Clear all filters and refetch both analytics and submissions
        const clearAllFilters = () => {
            activeFilters.value = {};
            fetchAnalytics(); // Update charts to show all data
            fetchSubmissions(1);
        };

        // Get field name by ID
        const getFieldName = (fieldId) => {
            const field = survey.value?.form?.fields.find(f => f.id === parseInt(fieldId));
            return field ? field.label : '';
        };

        // Get option labels for filter chips
        const getOptionLabels = (fieldId, optionIds) => {
            const field = survey.value?.form?.fields.find(f => f.id === parseInt(fieldId));
            if (!field) return '';

            const labels = field.options
                .filter(opt => optionIds.includes(opt.id))
                .map(opt => opt.label);

            return labels.join(', ');
        };

        const chartsContainer = ref(null);

        const exportChartsAsPNG = async () => {
            if (!chartsContainer.value) return;

            try {
                const dataUrl = await toPng(chartsContainer.value, {
                    backgroundColor: '#ffffff',
                    pixelRatio: 2, // High quality 2x resolution
                    cacheBust: true,
                    skipFonts: true, // Skip external fonts to avoid CORS issues
                    skipAutoScale: true,
                    filter: (node) => {
                        // Exclude elements with 'no-export' class from the export
                        return !node.classList?.contains('no-export');
                    }
                });

                // Download the image
                const link = document.createElement('a');
                link.download = `${survey.value.title}_kaaviot_${Date.now()}.png`;
                link.href = dataUrl;
                link.click();
            } catch (error) {
                console.error('Error exporting charts:', error);
            }
        };

        const exportSingleChart = async (fieldId, fieldLabel) => {
            try {
                // Get the chart element using the ref
                const chartElement = document.querySelector(`[data-field-id="${fieldId}"]`)?.closest('.v-card');

                if (!chartElement) {
                    console.error('Chart element not found');
                    return;
                }

                const dataUrl = await toPng(chartElement, {
                    backgroundColor: '#ffffff',
                    pixelRatio: 2,
                    cacheBust: true,
                    skipFonts: true,
                    skipAutoScale: true,
                    filter: (node) => {
                        // Exclude elements with 'no-export' class from the export
                        return !node.classList?.contains('no-export');
                    }
                });

                // Download the image
                const link = document.createElement('a');
                link.download = `${fieldLabel}_${Date.now()}.png`;
                link.href = dataUrl;
                link.click();
            } catch (error) {
                console.error('Error exporting single chart:', error);
            }
        };

        const excelExport = async () => {
            try {
                if (loadingExport.value)
                    return;

                loadingExport.value = true;

                // Build filters parameter (same as used for submissions)
                const filtersParam = Object.keys(activeFilters.value).length > 0
                    ? JSON.stringify(activeFilters.value)
                    : null;

                // Call backend export endpoint via API method
                const response = await adminApi.exportSurveySubmissions(props.id, filtersParam);

                // Create download link
                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;

                // Generate default filename
                const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-').replace('T', '_');
                let filename = `${survey.value.title}_vastaukset_${timestamp}.xlsx`;

                // Try to extract filename from Content-Disposition header if available
                if (response.headers && response.headers['content-disposition']) {
                    const filenameMatch = response.headers['content-disposition'].match(/filename="?(.+)"?/i);
                    if (filenameMatch) {
                        filename = filenameMatch[1];
                    }
                }

                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(downloadUrl);
            } catch (error) {
                console.error('Error exporting submissions:', error);
            } finally {
                loadingExport.value = false;
            }
        };

        const goBack = () => {
            router.push({ name: "admin-survey-list" });
        };

        // Watch for analytics panel opening and lazy load analytics
        watch(analyticsPanel, (newVal) => {
            // Panel opened (value changes from null to 0) and analytics not yet loaded
            if (newVal === 0 && !analytics.value) {
                fetchAnalytics();
            }
        });

        onMounted(() => {
            fetchData();
        });

        return {
            survey,
            submissions,
            loading,
            loadingAnalytics,
            loadingExport,
            analyticsPanel,
            activeFilters,
            selectFields,
            lastResponseDate,
            getOptionCount,
            getOptionPercentage,
            filteredSubmissions,
            hasActiveFilters,
            isOptionFiltered,
            toggleFilter,
            removeFilter,
            clearAllFilters,
            getFieldName,
            getOptionLabels,
            goBack,
            excelExport,
            exportChartsAsPNG,
            exportSingleChart,
            chartsContainer,
            fetchSubmissions,
            totalCount,
            currentPage,
            pageSize,
            totalSubmissions,
            totalPages,
            getDateTime,
            getFormattedAnswer,
            icons: {
                mdiArrowLeft,
                mdiClipboardTextOffOutline,
                mdiAlertCircle,
                mdiChartBar,
                mdiMicrosoftExcel,
                mdiDownload,
            },
        };
    },
};
</script>

<style scoped>
#survey-submissions>>>table {
    white-space: nowrap;
}

#survey-submissions>>>th,
#survey-submissions>>>td {
    padding: 12px 16px !important;
}

#survey-submissions>>>td {
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
}

.clickable {
    cursor: pointer;
}

.clickable:hover {
    opacity: 0.85;
}

.active-filter {
    border: 2px solid #1565C0 !important;
    box-shadow: 0 2px 4px rgba(21, 101, 192, 0.3) !important;
}

.loading-analytics:hover {
    cursor: progress !important;
}
</style>
